/**
 * Fase 1 do Boleto Único — classificação de débito automático da Equatorial por UC.
 *
 * PARA QUE SERVE: o boleto único soma a parte da Equatorial ao boleto da Vigor. Quem
 * já é debitado direto pela distribuidora pagaria DUAS vezes. Antes de a Diretoria
 * decidir a política de adesão, precisamos do tamanho do problema: quantas UCs são
 * débito automático, quantas estão confirmadas fora dele e quantas simplesmente não
 * sabemos.
 *
 * POR QUE NÃO É SÓ `WHERE debito_automatico_equatorial = true`:
 *
 * 1. A flag é por FATURA, não por UC — o cliente pode entrar/sair do débito automático
 *    mês a mês. Daí a janela de N meses e o `ultimo_mes_avaliado`: o resultado tem
 *    prazo de validade, não é carimbo permanente na UC.
 * 2. `false` NÃO significa "confirmado que não tem". O detector é uma regex sobre o
 *    texto do PDF (`_shared/fatura-parser/boleto-pix.ts:16`, `detectaDebitoAutomatico`)
 *    e o mapeador grava `!!cab.debito_automatico` (`_shared/mapeador-faturas/index.ts:213`).
 *    Nunca devolve `null`: layout novo, PDF ruim ou OCR falho também viram `false`.
 *    Então `false` só vale como confirmação se houver prova de que o parser leu o bloco
 *    de cobrança da Equatorial direito.
 * 3. Essa prova é a existência de uma linha digitável extraída — 47 dígitos (boleto
 *    bancário/ficha de compensação) ou 48 (arrecadação de convênio). Se o parser
 *    conseguiu extrair e VALIDAR matematicamente uma linha de cobrança, ele leu o bloco;
 *    o `false` ali é uma leitura, não uma falha. Sem nenhuma linha, é `desconhecido`.
 *
 *    Os 48 dígitos JÁ FORAM tratados como suspeita de débito automático aqui, porque é o
 *    layout que a fatura em débito automático imprime (comentário do lote JUN/2026 em
 *    `boleto-pix.ts`). A auditoria de 12/09/2026 derrubou isso: os 38 PDFs suspeitos
 *    foram abertos um a um (`scripts/ver-suspeitas-48-digitos.ts`) e NENHUM era débito
 *    automático — em todos, a única menção ao termo era o convite de marketing da
 *    Equatorial ("Aproveite os benefícios do débito automático, cadastre-se..."), que a
 *    regex corretamente ignora. A Equatorial imprime arrecadação para cliente comum
 *    também: o tamanho da linha não separa nada. Não reintroduza essa suspeita sem
 *    evidência nova em PDF.
 *
 * Lógica pura de propósito: o script de levantamento
 * (`scripts/levantamento-debito-automatico-equatorial.ts`) e, depois, a pergunta do
 * fluxo de aceite (tarefa 3/6) usam esta mesma função em vez de reimplementar a regra
 * em SQL solto.
 */

import { ucCodigoKeys } from '../ucCodigo.ts';
import { mesReferenciaParaData } from '../financeiro/periodo.ts';

export type StatusDebitoAutomatico = 'confirmado_sim' | 'confirmado_nao' | 'desconhecido';

/**
 * Por que o status é esse. Vai na planilha da Diretoria: sem o motivo, "desconhecido"
 * não diz se falta fatura (problema de cobertura do robô) ou se a fatura chegou e não
 * deu para concluir (problema de parser).
 */
export type MotivoDebitoAutomatico =
  /** Flag `true` em pelo menos uma fatura da janela. */
  | 'flag_debito_automatico'
  /** Flag `false` + linha digitável de 47 dígitos (boleto bancário existe). */
  | 'boleto_bancario_presente'
  /** Flag `false` + linha de arrecadação de 48 dígitos (também é cobrança para pagar). */
  | 'linha_arrecadacao_presente'
  /** Nenhuma fatura na janela — robô nunca leu, UC nova ou sem cobrança. */
  | 'sem_fatura_na_janela'
  /** Faturas na janela, mas todas com a flag `NULL` (anteriores à coluna). */
  | 'flag_ausente'
  /** Flag `false` e nenhuma linha digitável extraída — parser não leu o bloco. */
  | 'sem_dados_de_cobranca';

/** Só o que a classificação lê de `faturas`. */
export interface FaturaDebitoAutomatico {
  uc_codigo?: string | null;
  /** Coluna gerada em `faturas`; quando ausente, normalizamos `uc_codigo`. */
  uc_codigo_norm?: string | null;
  mes_referencia?: string | null;
  debito_automatico_equatorial?: boolean | null;
  linha_digitavel_equatorial?: string | null;
  pix_copia_cola_equatorial?: string | null;
}

/** Só o que a classificação lê de `unidades_consumidoras`. */
export interface UcDebitoAutomatico {
  id?: number;
  numero_uc?: string | null;
  /** Segundo número da MESMA unidade física — sem ele metade das faturas não casa. */
  outro_numero_uc?: string | null;
  nome_cliente?: string | null;
  situacao?: string | null;
  boleto_unico?: boolean | null;
}

export interface ClassificacaoUc {
  uc_id?: number;
  uc_codigo: string;
  outro_numero_uc: string | null;
  cliente: string;
  situacao: string | null;
  /** Já marcada para boleto único hoje — quem está aqui e é `confirmado_sim` é risco imediato. */
  boleto_unico: boolean;
  status: StatusDebitoAutomatico;
  motivo: MotivoDebitoAutomatico;
  /** Faturas da UC dentro da janela. */
  faturas_na_janela: number;
  /** Quantas delas com a flag `true`. */
  meses_com_debito_automatico: number;
  /** Meses (`mes_referencia`) com a flag `true`, do mais recente para o mais antigo. */
  meses_debito_automatico: string[];
  /** Mês de referência mais recente avaliado — prazo de validade da conclusão. */
  ultimo_mes_avaliado: string | null;
}

export interface ResumoLevantamento {
  total_ucs: number;
  confirmado_sim: number;
  confirmado_nao: number;
  desconhecido: number;
  /** Quebra do `desconhecido` por motivo — separa cobertura do robô de falha de parser. */
  desconhecido_por_motivo: Record<string, number>;
  /**
   * Quantas UCs JÁ marcadas com `boleto_unico = true` caem em cada status. É o corte
   * que separa o universo teórico do problema que já está ligado em produção.
   */
  marcadas_boleto_unico_por_status: Record<StatusDebitoAutomatico, number>;
  /** UCs hoje com `boleto_unico = true` e `confirmado_sim`: cobrança em dobro já possível. */
  risco_imediato: number;
}

/**
 * Exportado para o teste de paridade com o CASE de `aceitar_boleto_unico_portal`
 * (migration 20260914150000) — as duas listas têm que casar, ou a função do banco
 * aceita/recusa UC que aqui aparece com a situação oposta.
 */
export const SITUACOES_ELEGIVEIS = new Set(['Ativa', 'Ativa-S/-Rateio', '1° Ciclo', '2° Ciclo', 'Reativacao']);

/** UC que pode entrar no boleto único — cancelada/pausada não interessa à política. */
export function ucElegivel(uc: UcDebitoAutomatico): boolean {
  return SITUACOES_ELEGIVEIS.has((uc.situacao || '').trim());
}

/** Só dígitos: a linha vem com pontos, espaços e hífens do texto do PDF. */
const somenteDigitos = (v?: string | null): string => (v || '').replace(/\D/g, '');

/**
 * Janela de `meses` competências terminando no mês de `hoje` (inclusive).
 * Devolve chaves `AAAA-MM` porque `mes_referencia` é texto ('JUL/2026') e ordenar
 * string daria AGO antes de JUL.
 */
export function janelaMeses(hoje: Date, meses: number): Set<string> {
  const chaves = new Set<string>();
  for (let i = 0; i < Math.max(1, meses); i++) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    chaves.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return chaves;
}

const chaveMes = (mesRef?: string | null): string | null => {
  const d = mesReferenciaParaData(mesRef);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const chaveFatura = (f: FaturaDebitoAutomatico): string => {
  const norm = (f.uc_codigo_norm || '').trim();
  if (norm) return norm;
  return ucCodigoKeys(f.uc_codigo)[0] || '';
};

/** Indexa as faturas por chave normalizada de UC — uma passada, não N buscas. */
export function indexarFaturasPorUc(
  faturas: FaturaDebitoAutomatico[]
): Map<string, FaturaDebitoAutomatico[]> {
  const mapa = new Map<string, FaturaDebitoAutomatico[]>();
  for (const f of faturas) {
    const chave = chaveFatura(f);
    if (!chave) continue;
    const lista = mapa.get(chave);
    if (lista) lista.push(f);
    else mapa.set(chave, [f]);
  }
  return mapa;
}

export function classificarUc(
  uc: UcDebitoAutomatico,
  faturasPorUc: Map<string, FaturaDebitoAutomatico[]>,
  janela: Set<string>
): ClassificacaoUc {
  const chaves = ucCodigoKeys(uc.numero_uc, uc.outro_numero_uc);

  // A mesma fatura pode aparecer por `numero_uc` e por `outro_numero_uc` quando os
  // dois normalizam igual (acontece em produção) — deduplica por identidade.
  const vistas = new Set<FaturaDebitoAutomatico>();
  const naJanela: { f: FaturaDebitoAutomatico; mes: string; ordem: string }[] = [];
  for (const chave of chaves) {
    for (const f of faturasPorUc.get(chave) || []) {
      if (vistas.has(f)) continue;
      vistas.add(f);
      const ordem = chaveMes(f.mes_referencia);
      if (!ordem || !janela.has(ordem)) continue;
      naJanela.push({ f, mes: (f.mes_referencia || '').toUpperCase(), ordem });
    }
  }
  naJanela.sort((a, b) => b.ordem.localeCompare(a.ordem));

  const comDebito = naJanela.filter(x => x.f.debito_automatico_equatorial === true);
  const base = {
    uc_id: uc.id,
    uc_codigo: (uc.numero_uc || '').trim(),
    outro_numero_uc: (uc.outro_numero_uc || '').trim() || null,
    cliente: (uc.nome_cliente || '').trim(),
    situacao: (uc.situacao || '').trim() || null,
    boleto_unico: uc.boleto_unico === true,
    faturas_na_janela: naJanela.length,
    meses_com_debito_automatico: comDebito.length,
    meses_debito_automatico: comDebito.map(x => x.mes),
    ultimo_mes_avaliado: naJanela[0]?.mes ?? null,
  };

  if (comDebito.length > 0) {
    return { ...base, status: 'confirmado_sim', motivo: 'flag_debito_automatico' };
  }
  if (naJanela.length === 0) {
    return { ...base, status: 'desconhecido', motivo: 'sem_fatura_na_janela' };
  }

  const comFlagFalse = naJanela.filter(x => x.f.debito_automatico_equatorial === false);
  if (comFlagFalse.length === 0) {
    // Todas com a flag NULL: faturas anteriores à coluna (16/07/2026) ou que nunca
    // passaram pelo mapeador novo.
    return { ...base, status: 'desconhecido', motivo: 'flag_ausente' };
  }

  // `false` só confirma com prova de que o bloco de cobrança foi lido: uma linha
  // digitável válida, de 47 (ficha de compensação) ou 48 dígitos (arrecadação). O
  // motivo distingue as duas para auditoria, mas ambas concluem o mesmo.
  const temBoletoBancario = comFlagFalse.some(
    x => somenteDigitos(x.f.linha_digitavel_equatorial).length === 47
  );
  if (temBoletoBancario) {
    return { ...base, status: 'confirmado_nao', motivo: 'boleto_bancario_presente' };
  }

  const temArrecadacao = comFlagFalse.some(
    x => somenteDigitos(x.f.linha_digitavel_equatorial).length === 48
  );
  if (temArrecadacao) {
    return { ...base, status: 'confirmado_nao', motivo: 'linha_arrecadacao_presente' };
  }

  return { ...base, status: 'desconhecido', motivo: 'sem_dados_de_cobranca' };
}

export function classificarLevantamento(
  ucs: UcDebitoAutomatico[],
  faturas: FaturaDebitoAutomatico[],
  opcoes: { hoje: Date; meses?: number }
): ClassificacaoUc[] {
  const janela = janelaMeses(opcoes.hoje, opcoes.meses ?? 3);
  const porUc = indexarFaturasPorUc(faturas);
  return ucs.map(uc => classificarUc(uc, porUc, janela));
}

export function resumirLevantamento(linhas: ClassificacaoUc[]): ResumoLevantamento {
  const resumo: ResumoLevantamento = {
    total_ucs: linhas.length,
    confirmado_sim: 0,
    confirmado_nao: 0,
    desconhecido: 0,
    desconhecido_por_motivo: {},
    marcadas_boleto_unico_por_status: { confirmado_sim: 0, confirmado_nao: 0, desconhecido: 0 },
    risco_imediato: 0,
  };
  for (const l of linhas) {
    resumo[l.status] += 1;
    if (l.boleto_unico) resumo.marcadas_boleto_unico_por_status[l.status] += 1;
    if (l.status === 'desconhecido') {
      resumo.desconhecido_por_motivo[l.motivo] =
        (resumo.desconhecido_por_motivo[l.motivo] || 0) + 1;
    }
    if (l.status === 'confirmado_sim' && l.boleto_unico) resumo.risco_imediato += 1;
  }
  return resumo;
}
